import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Hub, InvitationType, ListQueryOptions } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { HubUserRole, User } from '@shared/models/user.model';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { ManageUserAccessComponent } from '@app/main/hub/manage-user-access/manage-user-access.component';
import { SendInvitationComponent } from '@shared/components/send-invitation/send-invitation.component';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { switchMap, filter } from 'rxjs/operators';

@Component({
  selector: 'hub-users',
  templateUrl: './hub-users.component.html',
  styleUrls: ['./hub-users.component.scss']
})
export class HubUsersComponent implements OnInit {
  @Input() hub: Hub;
  @Input() isInTrial: boolean;
  @Input() planExpiredMessage: string;
  @Output() usersTotalChange = new EventEmitter<number>();

  hubUsers: User[];
  displayedColumns: string[] = ['name', 'email', 'role', 'actions'];
  currentSearch = '';
  currentRoleFilter: HubUserRole | null;
  isFetchingUsers = false;

  private _listUsersOption = new ListQueryOptions('-createdAt', null, 0, 30, true);
  private _hubUsersTotal = 0;

  @ViewChild(MatTable, { static: false }) table: MatTable<any>;

  constructor(
    private _courseService: CoursesAPIService,
    private _dialog: MatDialog,
    private _internalAnalyticsService: InternalAnalyticsService,
    private _authService: AuthenticationService
  ) {}

  get currentUserId(): string {
    return this._authService.wondaUser?.id;
  }

  ngOnInit(): void {
    this.setUserList(this.hub.urlTitle);
  }

  setUserList(hubUrlTitle: string): void {
    this.currentSearch = null;
    this._listUsersOption.search = null;
    this._listUsersOption.offset = 0;
    this._listUsersOption.total = true;
    this._courseService.getHubUsers(hubUrlTitle, this._listUsersOption).subscribe((paginatedUsers) => {
      this.hubUsers = paginatedUsers.users;
      this._hubUsersTotal = paginatedUsers.total;
      this.usersTotalChange.emit(this._hubUsersTotal);
      this._listUsersOption.total = false;
      this._listUsersOption.offset += this.hubUsers.length;
    });
  }

  editUser(user: User): void {
    const dialogRef = this._dialog.open(ManageUserAccessComponent, {
      width: '40vw',
      height: '50vh',
      panelClass: 'learnspace-theme'
    });
    dialogRef.componentInstance.user = user;
    dialogRef.componentInstance.hub = this.hub;
  }

  inviteNewUserToHub(): void {
    const sendInvitDialog = this._dialog.open(SendInvitationComponent, {
      width: '48vw',
      height: '62vh',
      panelClass: ['learnspace-theme', 'invite-new-user-dialog']
    });
    sendInvitDialog.componentInstance.instructionMessage = 'Invite new users to this Hub';
    sendInvitDialog.componentInstance.showInvitationLink = true;
    sendInvitDialog.componentInstance.manageRole = true;
    sendInvitDialog.componentInstance.defaultRole = HubUserRole.default;
    sendInvitDialog.componentInstance.feedback = true;
    sendInvitDialog.componentInstance.targetId = this.hub.id;
    sendInvitDialog.componentInstance.type = InvitationType.joinHub;
    sendInvitDialog.componentInstance.showDetailList = true;
    sendInvitDialog.componentInstance.target = this.hub;
    sendInvitDialog.componentInstance.done.subscribe(() => this._dialog.closeAll());
  }

  kickUser(user: User): void {
    this._dialog
      .open(ConfirmationDialogComponent, {
        width: '400px',
        height: 'auto',
        panelClass: 'learnspace-theme',
        data: {
          title: 'Unsubscribe this user',
          type: 'user',
          action: 'unsubscribe'
        }
      })
      .afterClosed()
      .pipe(
        filter((val) => val === 'ok'),
        switchMap(() => this._courseService.dissociateUserFromHub(this.hub.id, user.id))
      )
      .subscribe(() => {
        this.hubUsers = this.hubUsers.filter((u) => u.id !== user.id);
        this.table.renderRows();
      });
  }

  updateUserRole(event: { value: HubUserRole }, user: User): void {
    const index = this.hubUsers.findIndex((participant) => participant.id === user.id);
    const previousRole = this.hubUsers[index].HubsUsers.role;
    this.hubUsers[index].HubsUsers.role = event.value;
    this._courseService.associateUserToHub(this.hub.id, user.id, event.value).subscribe({
      error: () => {
        this.hubUsers[index].HubsUsers.role = previousRole;
      }
    });
  }

  contactSales(): void {
    this._internalAnalyticsService.showNewMessage();
  }

  onTableScroll(e: any): void {
    if (this.isFetchingUsers) return;
    const tableViewHeight = e.target.offsetHeight;
    const tableScrollHeight = e.target.scrollHeight;
    const scrollLocation = e.target.scrollTop;
    const needToFetchRemainingUsers = this.hubUsers.length < this._hubUsersTotal;
    if (tableScrollHeight - tableViewHeight - scrollLocation < 5 && needToFetchRemainingUsers) {
      const _listUsersOption = new ListQueryOptions(this._listUsersOption.sort, this._listUsersOption.search, this._listUsersOption.offset, 10, true);
      this.isFetchingUsers = true;
      this._courseService.getHubUsers(this.hub.urlTitle, _listUsersOption).subscribe((paginatedHubUser) => {
        this.isFetchingUsers = false;
        this.hubUsers = this.hubUsers.concat(paginatedHubUser.users);
        this._listUsersOption.offset = this.hubUsers.length;
      });
    }
  }

  setSearch(): void {
    if (this.isFetchingUsers) return;
    this._applyFilters();
  }

  clearSearch(): void {
    if (this.isFetchingUsers) return;
    this.currentSearch = null;
    this._applyFilters();
  }

  onRoleFilterChange(): void {
    if (this.isFetchingUsers) return;
    this._applyFilters();
  }

  getRoleIcon(role: string): string {
    const icons: Record<string, string> = { admin: 'fa-user-gear', manager: 'fa-user-pen', user: 'fa-user' };
    return icons[role] ?? 'fa-user';
  }

  getRoleLabel(role: string): string {
    const labels: Record<string, string> = { admin: 'Admin', manager: 'Manager', user: 'Default' };
    return labels[role] ?? role;
  }

  private _buildSearchQuery(): string | null {
    if (!this.currentSearch) return null;
    return `email:%${this.currentSearch}%|first_name:%${this.currentSearch}%|last_name:%${this.currentSearch}%`;
  }

  private _applyFilters(): void {
    this._listUsersOption.search = this._buildSearchQuery();
    this._listUsersOption.role = this.currentRoleFilter || undefined;
    this._listUsersOption.offset = 0;
    this._listUsersOption.total = true;
    this._courseService.getHubUsers(this.hub.urlTitle, this._listUsersOption).subscribe((paginatedHubUser) => {
      this._hubUsersTotal = paginatedHubUser.total;
      this.usersTotalChange.emit(this._hubUsersTotal);
      this.hubUsers = paginatedHubUser.users;
      this._listUsersOption.offset = this.hubUsers.length;
    });
  }
}
